/**
 * NEC code edition support.
 *
 * Every table and rule in the engine is written to NEC 2023 (NFPA 70-2023)
 * and every citation string in the app is a 2023 citation. For the rules the
 * calculators enforce, the numeric requirements are identical in the 2017,
 * 2020 and 2023 editions; what changed between editions is the numbering of
 * a handful of sections and tables. The edition setting therefore only
 * changes how citations are shown (and printed/exported) — never a result.
 *
 * `NEC_CITATION_RENUMBERING` is keyed by the 2023 citation fragment and
 * gives the 2020 and 2017 equivalents. `applyNecEdition` rewrites free text
 * longest-match-first so a longer citation such as "Table 310.15(B)(1)(1)"
 * is never partially rewritten by a shorter one such as "310.15(B)(1)".
 */

export type NecEdition = '2017' | '2020' | '2023';

export const NEC_EDITIONS: NecEdition[] = ['2017', '2020', '2023'];

export const DEFAULT_NEC_EDITION: NecEdition = '2023';

export const NEC_EDITION_LABELS: Record<NecEdition, string> = {
  '2017': 'NEC 2017 (NFPA 70-2017)',
  '2020': 'NEC 2020 (NFPA 70-2020)',
  '2023': 'NEC 2023 (NFPA 70-2023)',
};

export function isNecEdition(value: unknown): value is NecEdition {
  return typeof value === 'string' && (NEC_EDITIONS as string[]).includes(value);
}

export interface NecCitationRenumbering {
  /** The citation fragment as it appears in the app (NEC 2023 numbering). */
  '2023': string;
  '2020': string;
  '2017': string;
  /** What the citation refers to, for documentation and the Settings panel. */
  topic: string;
}

/**
 * Citation renumbering between editions, keyed by the 2023 fragment.
 *
 * Rows whose three values are identical are documented here so the audit is
 * complete; they are skipped when building the rewrite pattern.
 */
export const NEC_CITATION_RENUMBERING: readonly NecCitationRenumbering[] = [
  // ---- Article 310: conductor ampacity (restructured in 2020; 2023 kept the 2020 numbering)
  { '2023': 'Table 310.16', '2020': 'Table 310.16', '2017': 'Table 310.15(B)(16)', topic: 'Conductor ampacity, 0–2000 V, 30 °C ambient' },
  { '2023': '310.16', '2020': '310.16', '2017': '310.15(B)(16)', topic: 'Conductor ampacity table (section reference)' },
  // The 30 °C correction table is "Table 310.15(B)(1)(1)" in both 2020 and 2023 (the 40 °C
  // table is (B)(1)(2)); the app cites the shorter "Table 310.15(B)(1)", which is the same in
  // both of those editions, so only 2017 differs.
  { '2023': 'Table 310.15(B)(1)(1)', '2020': 'Table 310.15(B)(1)(1)', '2017': 'Table 310.15(B)(2)(a)', topic: 'Ambient temperature correction, 30 °C basis' },
  { '2023': 'Table 310.15(B)(1)(2)', '2020': 'Table 310.15(B)(1)(2)', '2017': 'Table 310.15(B)(2)(b)', topic: 'Ambient temperature correction, 40 °C basis' },
  { '2023': 'Table 310.15(B)(1)', '2020': 'Table 310.15(B)(1)', '2017': 'Table 310.15(B)(2)(a)', topic: 'Ambient temperature correction, 30 °C basis' },
  { '2023': '310.15(B)(1)', '2020': '310.15(B)(1)', '2017': '310.15(B)(2)', topic: 'Ambient temperature correction factors' },
  { '2023': 'Table 310.15(C)(1)', '2020': 'Table 310.15(C)(1)', '2017': 'Table 310.15(B)(3)(a)', topic: 'Adjustment for more than three current-carrying conductors' },
  { '2023': '310.15(C)(1)', '2020': '310.15(C)(1)', '2017': '310.15(B)(3)(a)', topic: 'Adjustment for more than three current-carrying conductors' },
  { '2023': '310.15(C)', '2020': '310.15(C)', '2017': '310.15(B)(3)', topic: 'Adjustment factors' },
  { '2023': '310.15(B)(2)', '2020': '310.15(B)(2)', '2017': '310.15(B)(3)(c)', topic: 'Raceways and cables exposed to sunlight on rooftops' },
  { '2023': '310.10(G)', '2020': '310.10(G)', '2017': '310.10(H)', topic: 'Conductors in parallel' },

  // ---- Article 220 Part III (renumbered in 2023; 2017 and 2020 share the older numbers)
  { '2023': 'Table 220.45', '2020': 'Table 220.42', '2017': 'Table 220.42', topic: 'Lighting load demand factors' },
  { '2023': '220.45', '2020': '220.42', '2017': '220.42', topic: 'Lighting load demand factors' },
  { '2023': 'Table 220.47', '2020': 'Table 220.44', '2017': 'Table 220.44', topic: 'Receptacle loads, other than dwelling units' },
  { '2023': '220.47', '2020': '220.44', '2017': '220.44', topic: 'Receptacle loads, other than dwelling units' },
  { '2023': 'Table 220.55', '2020': 'Table 220.55', '2017': 'Table 220.55', topic: 'Household cooking equipment demand' },
  { '2023': '220.61', '2020': '220.61', '2017': '220.61', topic: 'Feeder or service neutral load' },

  // ---- Article 311 (new in 2023): medium-voltage conductors, previously 310.60
  { '2023': 'Table 311.60(C)', '2020': 'Table 310.60(C)', '2017': 'Table 310.60(C)', topic: 'Medium-voltage cable ampacity tables' },
  { '2023': '311.60', '2020': '310.60', '2017': '310.60', topic: 'Medium-voltage conductor ampacity' },

  // ---- Unchanged across 2017 / 2020 / 2023
  { '2023': '110.14(C)', '2020': '110.14(C)', '2017': '110.14(C)', topic: 'Temperature limitations at terminations' },
  { '2023': '240.4(B)', '2020': '240.4(B)', '2017': '240.4(B)', topic: 'Next-higher standard OCPD rating' },
  { '2023': '240.4(C)', '2020': '240.4(C)', '2017': '240.4(C)', topic: 'OCPD over 800 A' },
  { '2023': '240.4(D)', '2020': '240.4(D)', '2017': '240.4(D)', topic: 'Small conductors' },
  { '2023': '240.4(G)', '2020': '240.4(G)', '2017': '240.4(G)', topic: 'Specific conductor applications' },
  { '2023': 'Table 240.6(A)', '2020': 'Table 240.6(A)', '2017': 'Table 240.6(A)', topic: 'Standard ampere ratings' },
  { '2023': '250.66', '2020': '250.66', '2017': '250.66', topic: 'Grounding electrode conductor' },
  { '2023': '250.102(C)(1)', '2020': '250.102(C)(1)', '2017': '250.102(C)(1)', topic: 'Supply-side bonding jumper' },
  { '2023': 'Table 250.122', '2020': 'Table 250.122', '2017': 'Table 250.122', topic: 'Equipment grounding conductor' },
  { '2023': '250.122(B)', '2020': '250.122(B)', '2017': '250.122(B)', topic: 'EGC increased in size' },
  { '2023': '430.6(A)(1)', '2020': '430.6(A)(1)', '2017': '430.6(A)(1)', topic: 'Motor table values vs. nameplate' },
  { '2023': '430.22', '2020': '430.22', '2017': '430.22', topic: 'Single motor conductors' },
  { '2023': '430.24', '2020': '430.24', '2017': '430.24', topic: 'Several motors, conductors' },
  { '2023': '430.32', '2020': '430.32', '2017': '430.32', topic: 'Motor overload protection' },
  { '2023': '430.52', '2020': '430.52', '2017': '430.52', topic: 'Motor branch-circuit short-circuit and ground-fault protection' },
  { '2023': 'Table 430.52(C)(1)', '2020': 'Table 430.52(C)(1)', '2017': 'Table 430.52(C)(1)', topic: 'Maximum rating of motor branch-circuit protection' },
  { '2023': '430.62', '2020': '430.62', '2017': '430.62', topic: 'Motor feeder protection' },
  { '2023': '430.83', '2020': '430.83', '2017': '430.83', topic: 'Controller rating' },
  { '2023': '430.110', '2020': '430.110', '2017': '430.110', topic: 'Disconnecting means ampere rating' },
  { '2023': 'Table 430.248', '2020': 'Table 430.248', '2017': 'Table 430.248', topic: 'Single-phase motor full-load currents' },
  { '2023': 'Table 430.250', '2020': 'Table 430.250', '2017': 'Table 430.250', topic: 'Three-phase motor full-load currents' },
  { '2023': 'Table 450.3(A)', '2020': 'Table 450.3(A)', '2017': 'Table 450.3(A)', topic: 'Transformer protection over 1000 V' },
  { '2023': 'Table 450.3(B)', '2020': 'Table 450.3(B)', '2017': 'Table 450.3(B)', topic: 'Transformer protection 1000 V and less' },
  { '2023': '501.10', '2020': '501.10', '2017': '501.10', topic: 'Class I wiring methods' },
  { '2023': '501.15', '2020': '501.15', '2017': '501.15', topic: 'Class I sealing and drainage' },
  { '2023': '501.30', '2020': '501.30', '2017': '501.30', topic: 'Class I grounding and bonding' },
  { '2023': '501.125', '2020': '501.125', '2017': '501.125', topic: 'Class I motors and generators' },
  { '2023': 'Chapter 9 Table 1', '2020': 'Chapter 9 Table 1', '2017': 'Chapter 9 Table 1', topic: 'Percent of cross section of conduit' },
  { '2023': 'Chapter 9 Table 4', '2020': 'Chapter 9 Table 4', '2017': 'Chapter 9 Table 4', topic: 'Conduit dimensions' },
  { '2023': 'Chapter 9 Table 5', '2020': 'Chapter 9 Table 5', '2017': 'Chapter 9 Table 5', topic: 'Conductor dimensions' },
  { '2023': 'Chapter 9 Table 5A', '2020': 'Chapter 9 Table 5A', '2017': 'Chapter 9 Table 5A', topic: 'Compact conductor dimensions' },
  { '2023': 'Chapter 9 Table 8', '2020': 'Chapter 9 Table 8', '2017': 'Chapter 9 Table 8', topic: 'Conductor properties' },
  { '2023': 'Chapter 9 Table 9', '2020': 'Chapter 9 Table 9', '2017': 'Chapter 9 Table 9', topic: 'AC resistance and reactance' },
];

function escapeRegExp(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

interface EditionRewriter {
  pattern: RegExp;
  replacements: Map<string, string>;
}

const rewriterCache = new Map<NecEdition, EditionRewriter | null>();

/**
 * Build (once per edition) a single alternation of every 2023 fragment whose
 * target differs, longest first. A single pass means a rewritten fragment is
 * never re-scanned, so "310.15(B)(1)" → "310.15(B)(2)" (2017) cannot then be
 * mistaken for the 2023 rooftop citation "310.15(B)(2)".
 *
 * A fragment only matches when it is not glued to a preceding digit or dot
 * and not followed by a word character or ".digit", so "310.16" does not fire
 * inside "1310.16" or "310.160". A following "(" is allowed on purpose:
 * "311.60(C)(73)" must become "310.60(C)(73)". Longer citations that begin
 * with a shorter one have their own rows so the shorter one never wins.
 */
function getRewriter(edition: NecEdition): EditionRewriter | null {
  if (edition === '2023') return null;
  const cached = rewriterCache.get(edition);
  if (cached !== undefined) return cached;

  const replacements = new Map<string, string>();
  for (const row of NEC_CITATION_RENUMBERING) {
    const target = row[edition];
    if (target !== row['2023']) replacements.set(row['2023'], target);
  }
  const fragments = Array.from(replacements.keys()).sort((a, b) => b.length - a.length);
  const rewriter: EditionRewriter | null = fragments.length === 0
    ? null
    : {
        pattern: new RegExp(
          `(?<![\\d.])(?:${fragments.map(escapeRegExp).join('|')})(?!\\w|\\.\\d)`,
          'g',
        ),
        replacements,
      };
  rewriterCache.set(edition, rewriter);
  return rewriter;
}

/**
 * Rewrite every NEC 2023 citation in `text` to the numbering used by
 * `edition`. Also rewrites the literal prefixes "NEC 2023" and "NFPA 70-2023".
 * A no-op for 2023.
 */
export function applyNecEdition(text: string, edition: NecEdition): string {
  if (edition === '2023' || !text) return text;
  let out = text
    .replace(/\bNEC 2023\b/g, `NEC ${edition}`)
    .replace(/\bNFPA 70-2023\b/g, `NFPA 70-${edition}`);
  const rewriter = getRewriter(edition);
  if (rewriter) {
    out = out.replace(rewriter.pattern, (match) => rewriter.replacements.get(match) ?? match);
  }
  return out;
}

/**
 * What the edition setting does and does not change, for the Settings panel.
 */
export function necEditionDifferences(): Array<{ topic: string; note: string }> {
  return [
    {
      topic: 'Citations',
      note: 'Section and table numbers shown in results, calculation steps and NEC reference chips are renumbered to the selected edition.',
    },
    {
      topic: 'Print and export',
      note: 'Reference lists and section titles in printed or exported reports use the selected edition\'s numbering.',
    },
    {
      topic: 'Calculated values',
      note: 'Nothing. The ampacity, correction and adjustment, overcurrent, grounding, motor, transformer and Chapter 9 tables these calculators use carry the same values and the same rules in NEC 2017, 2020 and 2023.',
    },
    {
      topic: 'Article 310 (2020)',
      note: 'The 2020 edition renumbered the ampacity tables: Table 310.15(B)(16) became Table 310.16, ambient correction moved to Table 310.15(B)(1), the more-than-three-conductor adjustment to Table 310.15(C)(1), the rooftop adder to 310.15(B)(2) and parallel conductors from 310.10(H) to 310.10(G). 2023 kept the 2020 numbering.',
    },
    {
      topic: 'Article 220 (2023)',
      note: 'The 2023 edition renumbered Part III: lighting demand factors moved from Table 220.42 to Table 220.45 and non-dwelling receptacle demand from 220.44 to 220.47. 2017 and 2020 share the older numbers.',
    },
    {
      topic: 'Medium voltage (2023)',
      note: 'The 2023 edition moved conductors over 1000 V into new Article 311; Table 311.60(C)(73) and (74) were Table 310.60(C)(73) and (74) in 2017 and 2020.',
    },
    {
      topic: 'Not covered',
      note: 'Requirements these calculators do not evaluate (for example GFCI/AFCI, surge protection, emergency disconnects and the non-dwelling lighting VA per ft² in Table 220.12) did change between editions and are not adjusted by this setting.',
    },
  ];
}

// ---- Module-level active edition for non-React code (print/export helpers)

let activeEdition: NecEdition = DEFAULT_NEC_EDITION;

export function setActiveNecEdition(edition: NecEdition): void {
  activeEdition = isNecEdition(edition) ? edition : DEFAULT_NEC_EDITION;
}

export function getActiveNecEdition(): NecEdition {
  return activeEdition;
}
